import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Epic, Task, Requirement, Project } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Clock,
  Layers,
  Pencil,
  Plus,
  Search,
  Target,
  TrendingUp,
  Users,
  AlertCircle,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

import EditEpicModal from '../components/EditEpicModal';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { APP_FILTER_LABEL_CLASS, APP_NATIVE_FIELD_CLASS } from '../constants/formControls';
import { getPriorityColors, getStatusColors } from '../constants/colorScheme';
import { addEpic, addTask, updateEpic } from '../store';
import { generateId } from '../utils/idGenerator';
// Linking conventions: see docs/design/WORK_ITEM_LINKING_DESIGN.md


export default function EpicsView() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [editingEpicId, setEditingEpicId] = useState<string | null>(null);
  const [editingEpicTitle, setEditingEpicTitle] = useState('');
  const [quickTaskTitles, setQuickTaskTitles] = useState<Record<string, string>>({});
  const [collapsedMobileCards, setCollapsedMobileCards] = useState<Record<string, boolean>>({});
  const [isSavingEpicTitle, setIsSavingEpicTitle] = useState(false);
  const [creatingTaskEpicId, setCreatingTaskEpicId] = useState<string | null>(null);
  const [editingEpic, setEditingEpic] = useState<Epic | null>(null);
  const { state, loading, reload } = useApp();
  const { projects, tasks, epics } = useOrganizationData(state) as {
    projects: Project[];
    tasks: Task[];
    epics: Epic[];
  };

  const project = useMemo(() => {
      return projects.find(
        (p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()
      );
    }, [projects, projectId]);

  const projectRequirements = useMemo<Requirement[]>(
    () => project?.requirements || [],
    [project]
  );

  const requirementById = useMemo(() => {
    const map = new Map<string, Requirement>();
    projectRequirements.forEach((requirement) => map.set(requirement.id, requirement));
    return map;
  }, [projectRequirements]);

  const projectEpics = useMemo(
    () => epics.filter((e: Epic) => String(e.projectId) === String(projectId)),
    [epics, projectId]
  );
  
  if (loading && !project) {
    return <div className="p-8 text-center">Loading project...</div>;
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <p className="mb-4 text-muted-foreground">Project not found</p>
          <Link to="/app/projects" className="text-primary hover:text-primary/80">
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  // Get tasks for calculating epic stats
  const getEpicTasks = (epicId: string): Task[] => {
    return tasks.filter(t => t.epicId === epicId);
  };

  const getEpicStats = (epic: Epic) => {
    const tasks = getEpicTasks(epic.id);
    const completedTasks = tasks.filter(t => t.status === 'Completed').length;
    const totalTasks = tasks.length;
    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    
    const totalEstimatedHours = tasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);
    const totalActualHours = tasks.reduce((sum, t) => 
      sum + t.timeLogs.reduce((logSum, log) => logSum + log.hours, 0), 0
    );

    return {
      totalTasks,
      completedTasks,
      completionRate,
      totalEstimatedHours,
      totalActualHours,
    };
  };

  const isEpicCompleted = (epic: Epic): boolean => {
    const tasks = getEpicTasks(epic.id);
    if (tasks.length === 0) return false;
    return tasks.every(t => t.status === 'Completed');
  };

  // Filter epics
  const filteredEpics = projectEpics.filter((epic: Epic) => {
    const linkedRequirements = (epic.requirementIds || [])
      .map((id: string) => requirementById.get(id))
      .filter((req: Requirement | undefined): req is Requirement => Boolean(req));

    const requirementSearchBlob = linkedRequirements
      .map((req: Requirement) => `${req.code} ${req.title}`)
      .join(' ')
      .toLowerCase();

    const matchesSearch = epic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         epic.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         requirementSearchBlob.includes(searchQuery.toLowerCase());
    
    if (statusFilter === 'completed') {
      return matchesSearch && isEpicCompleted(epic);
    } else if (statusFilter === 'active') {
      return matchesSearch && !isEpicCompleted(epic);
    }
    
    return matchesSearch;
  });

  // Calculate overall stats
  const totalEpics = projectEpics.length;
  const completedEpics = projectEpics.filter((e: Epic) => isEpicCompleted(e)).length;
  const activeEpics = totalEpics - completedEpics;
  const overallCompletionRate = totalEpics > 0 ? (completedEpics / totalEpics) * 100 : 0;

  const getStatusColor = (completionRate: number): string => {
    if (completionRate === 100) {
      const status = getStatusColors('Completed');
      return `border ${status.container} ${status.text}`;
    }
    if (completionRate >= 75) {
      const status = getStatusColors('Active');
      return `border ${status.container} ${status.text}`;
    }
    if (completionRate >= 50) {
      const status = getStatusColors('Deferred');
      return `border ${status.container} ${status.text}`;
    }
    if (completionRate >= 25) {
      return `border ${getPriorityColors('High').badge}`;
    }
    const status = getStatusColors('Not Started');
    return `border ${status.container} ${status.text}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (deadline?: string): boolean => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const handleCreateEpic = async () => {
    if (!project) {
      toast.error('Project not found');
      return;
    }

    const orgId = project?.organizationId || state?.currentOrganization?.id || state?.currentUser?.organizationId;
    if (!orgId) {
      toast.error('Unable to determine organization');
      return;
    }

    try {
      const newEpic: Epic = {
        id: generateId('epic', projectId),
        organizationId: orgId,
        projectId: projectId!,
        title: 'New Epic',
        description: '',
        status: 'Not Started',
        taskIds: [],
        standupNotes: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      await addEpic(newEpic);
      toast.success('Epic created successfully');
      
      // Refresh state
      const email = state?.currentUser?.email;
      if (email) {
        await reload(email);
      }
    } catch (error: any) {
      console.error('Error creating epic:', error);
      toast.error(error.message || 'Failed to create epic');
    }
  };

  const startEditingEpicTitle = (epic: Epic) => {
    setEditingEpicId(epic.id);
    setEditingEpicTitle(epic.title || '');
    setCollapsedMobileCards((prev) => ({ ...prev, [epic.id]: false }));
  };

  const cancelEditingEpicTitle = () => {
    setEditingEpicId(null);
    setEditingEpicTitle('');
  };

  const handleSaveEpicTitle = async (epic: Epic) => {
    const nextTitle = editingEpicTitle.trim();
    if (!nextTitle) {
      toast.error('Epic title cannot be empty');
      return;
    }

    if (nextTitle === (epic.title || '').trim()) {
      cancelEditingEpicTitle();
      return;
    }

    try {
      setIsSavingEpicTitle(true);
      await updateEpic(epic.id, {
        title: nextTitle,
        updatedAt: new Date().toISOString(),
      });

      const email = state?.currentUser?.email;
      if (email) {
        await reload(email);
      }

      toast.success('Epic title updated');
      cancelEditingEpicTitle();
    } catch (error: any) {
      console.error('Error updating epic title:', error);
      toast.error(error.message || 'Failed to update epic title');
    } finally {
      setIsSavingEpicTitle(false);
    }
  };

  const handleQuickTaskTitleChange = (epicId: string, value: string) => {
    setQuickTaskTitles((prev) => ({ ...prev, [epicId]: value }));
  };

  const isCardCollapsedOnMobile = (epicId: string): boolean => {
    return collapsedMobileCards[epicId] ?? true;
  };

  const toggleMobileCard = (epicId: string) => {
    setCollapsedMobileCards((prev) => ({
      ...prev,
      [epicId]: !isCardCollapsedOnMobile(epicId),
    }));
  };

  const handleSaveEpicEdits = async (updates: Partial<Epic>) => {
    if (!editingEpic) return;
    try {
      await updateEpic(editingEpic.id, updates);
      const email = state?.currentUser?.email;
      if (email) {
        await reload(email);
      }
      toast.success('Epic updated');
      setEditingEpic(null);
    } catch (error: any) {
      console.error('Error updating epic:', error);
      toast.error(error?.message || 'Failed to update epic');
      throw error;
    }
  };

  const handleQuickAddTask = async (epic: Epic) => {
    const title = (quickTaskTitles[epic.id] || '').trim();
    if (!title) {
      toast.error('Task title is required');
      return;
    }

    const orgId = project?.organizationId || state?.currentOrganization?.id || state?.currentUser?.organizationId;
    if (!orgId || !projectId) {
      toast.error('Unable to determine project context');
      return;
    }

    try {
      setCreatingTaskEpicId(epic.id);
      const now = new Date().toISOString();
      // Pre-generate the task id so we can append it to epic.taskIds (denormalized cache)
      const newTaskId = generateId('task', projectId, epic.id);
      const newTask: Task = {
        id: newTaskId,
        organizationId: orgId,
        projectId,
        epicId: epic.id,
        title,
        description: '',
        type: 'Feature',
        status: 'Not Started',
        priority: 'Medium',
        assigneeIds: [],
        parentTaskIds: [],
        subtaskIds: [],
        estimatedDays: 0,
        timeLogs: [],
        standupNotes: [],
        createdAt: now,
        updatedAt: now,
        customerVisible: false,
        attachments: [],
        verificationLinks: [],
        externalLinks: [],
        costItemIds: [],
        changeLogs: [],
      } as Task;

      await addTask(newTask);

      // Keep the epic.taskIds cache in sync (Task.epicId remains the source of truth)
      const existingTaskIds = epic.taskIds || [];
      if (!existingTaskIds.includes(newTaskId)) {
        await updateEpic(epic.id, {
          taskIds: [...existingTaskIds, newTaskId],
          updatedAt: now,
        });
      }

      const email = state?.currentUser?.email;
      if (email) {
        await reload(email);
      }

      setQuickTaskTitles((prev) => ({ ...prev, [epic.id]: '' }));
      toast.success('Task added to epic');
    } catch (error: any) {
      console.error('Error creating quick task:', error);
      toast.error(error.message || 'Failed to add task');
    } finally {
      setCreatingTaskEpicId(null);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="mb-1 flex items-center gap-3 text-2xl font-semibold text-foreground">
              <Layers className="h-8 w-8 text-primary" />
              Epics
            </h1>
            <p className="text-muted-foreground">{project.name}</p>
          </div>
          <Button
            onClick={handleCreateEpic}
            className="inline-flex items-center gap-2 px-4 py-2"
          >
            <Plus className="h-4 w-4" />
            New Epic
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Total Epics</p>
                  <p className="text-2xl font-bold text-foreground">{totalEpics}</p>
                </div>
                <Package className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-foreground">{activeEpics}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-foreground">{completedEpics}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="mb-1 text-sm text-muted-foreground">Overall Progress</p>
                  <p className="text-2xl font-bold text-foreground">
                    {overallCompletionRate.toFixed(0)}%
                  </p>
                </div>
                <Target className="h-8 w-8 text-primary opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <span className={APP_FILTER_LABEL_CLASS}>Search</span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search epics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="space-y-1.5 sm:w-48">
            <span className={APP_FILTER_LABEL_CLASS}>Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              title="Filter epics by status"
              aria-label="Filter epics by status"
              className={`w-full text-sm ${APP_NATIVE_FIELD_CLASS}`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Epics List */}
      {filteredEpics.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Layers className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-xl font-semibold text-foreground">
              {searchQuery || statusFilter !== 'all' ? 'No epics found' : 'No epics yet'}
            </h3>
            <p className="mb-4 text-muted-foreground">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your filters'
                : 'Epics help organize tasks into larger initiatives'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredEpics.map((epic: Epic) => {
            const stats = getEpicStats(epic);
            const completed = isEpicCompleted(epic);
            const overdue = isOverdue(epic.targetEndDate);
            const isCollapsedMobile = isCardCollapsedOnMobile(epic.id);
            const linkedRequirements = (epic.requirementIds || [])
              .map((id: string) => requirementById.get(id))
              .filter((req: Requirement | undefined): req is Requirement => Boolean(req));
            
            return (
              <Card 
                key={epic.id} 
                className={`hover:shadow-lg transition-shadow border-l-4 ${
                  completed 
                    ? 'border-l-chart-2' 
                    : overdue 
                    ? 'border-l-destructive' 
                    : 'border-l-chart-4'
                }`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:gap-3">
                    {/* Main Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="mb-2 flex items-center gap-2">
                            {editingEpicId === epic.id ? (
                              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
                                <Input
                                  value={editingEpicTitle}
                                  onChange={(e) => setEditingEpicTitle(e.target.value)}
                                  placeholder="Epic title"
                                  className="h-9"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      void handleSaveEpicTitle(epic);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelEditingEpicTitle();
                                    }
                                  }}
                                  autoFocus
                                />
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => void handleSaveEpicTitle(epic)}
                                    disabled={isSavingEpicTitle}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={cancelEditingEpicTitle}
                                    disabled={isSavingEpicTitle}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="text-left text-lg font-semibold text-foreground hover:underline"
                                  onClick={() => startEditingEpicTitle(epic)}
                                  title="Click to rename epic"
                                  aria-label={`Rename ${epic.title}`}
                                >
                                  {epic.title}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setEditingEpic(epic)}
                                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                                  title="Edit epic details"
                                  aria-label={`Edit details for ${epic.title}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  Edit
                                </button>
                              </div>
                            )}
                            {completed && (
                              <Badge className={`border ${getStatusColors('Completed').badge}`}>
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Completed
                              </Badge>
                            )}
                            {overdue && !completed && (
                              <Badge className={`border ${getStatusColors('Blocked').badge}`}>
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Overdue
                              </Badge>
                            )}
                          </div>
                          
                          {epic.description && (
                                                      <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                              {epic.description}
                            </p>
                          )}

                          {/* Metadata */}
                                                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                            {epic.targetEndDate && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span className={overdue && !completed ? `${getStatusColors('Blocked').text} font-semibold` : ''}>
                                  {formatDate(epic.targetEndDate)}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>{stats.totalTasks} tasks</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>
                                {stats.totalActualHours.toFixed(1)}h / {stats.totalEstimatedHours.toFixed(1)}h
                              </span>
                            </div>
                          </div>

                          {linkedRequirements.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {linkedRequirements.map((requirement: Requirement) => (
                                <span
                                  key={requirement.id}
                                  className="inline-flex items-center gap-1 rounded border border-border bg-muted/60 px-2 py-1 text-xs text-foreground"
                                >
                                  <span className="font-mono">{requirement.code}</span>
                                  <span className="truncate max-w-[180px]">{requirement.title}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className={`mb-3 ${isCollapsedMobile ? 'hidden md:block' : 'block'}`}>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-foreground">
                            Progress: {stats.completedTasks} / {stats.totalTasks} tasks
                          </span>
                          <span className={`text-sm font-semibold ${
                            stats.completionRate === 100 ? getStatusColors('Completed').text :
                            stats.completionRate >= 75 ? getStatusColors('Active').text :
                            stats.completionRate >= 50 ? getStatusColors('Deferred').text :
                            getStatusColors('Not Started').text
                          }`}>
                            {stats.completionRate.toFixed(0)}%
                          </span>
                        </div>
                        <Progress
                          value={stats.completionRate}
                          className="h-2 [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
                        />
                      </div>

                      {/* View Tasks Button */}
                      <div className={`flex flex-col gap-2 sm:flex-row sm:items-center ${isCollapsedMobile ? 'hidden md:flex' : 'flex'}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/app/projects/${projectId}/spreadsheet?epic=${epic.id}`)}
                        >
                          <Layers className="w-4 h-4 mr-2" />
                          View Tasks
                        </Button>
                        <div className="flex w-full gap-2 sm:w-auto sm:min-w-[360px]">
                          <Input
                            value={quickTaskTitles[epic.id] || ''}
                            onChange={(e) => handleQuickTaskTitleChange(epic.id, e.target.value)}
                            placeholder="Quick add task title"
                            className="h-9"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleQuickAddTask(epic);
                              }
                            }}
                            aria-label={`Quick add task for ${epic.title}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => void handleQuickAddTask(epic)}
                            disabled={creatingTaskEpicId === epic.id || !(quickTaskTitles[epic.id] || '').trim()}
                            className="inline-flex items-center gap-1"
                          >
                            <Plus className="h-4 w-4" />
                            Add Task
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className={`grid grid-cols-2 gap-2 lg:w-32 lg:grid-cols-1 ${isCollapsedMobile ? 'hidden md:grid' : 'grid'}`}>
                      <Card className={getStatusColor(stats.completionRate)}>
                        <CardContent className="p-2">
                          <div className="text-center">
                            <div className="text-lg font-bold">
                              {stats.completedTasks}
                            </div>
                            <div className="text-xs">Completed</div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="border border-border bg-muted/40">
                        <CardContent className="p-2">
                          <div className="text-center">
                            <div className="text-lg font-bold text-foreground">
                              {stats.totalTasks - stats.completedTasks}
                            </div>
                            <div className="text-xs text-muted-foreground">Remaining</div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editingEpic && (
        <EditEpicModal
          epic={editingEpic}
          requirements={projectRequirements}
          onClose={() => setEditingEpic(null)}
          onSave={handleSaveEpicEdits}
        />
      )}
    </div>
  );
}
